var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { useCurrentAccount } from '@mysten/dapp-kit-react';
import { useCurrentWallet, useSignAndExecuteTransaction } from '../hooks/walletCompat';
import { AlphaStakingHero, AlphaTokenFlywheel, AlphaVaultSummary, RealYieldTable, TokenDistributionSupplySection, ClaimRewards, NotificationComp, convertToKAndM, GrowthMetrics, YieldBanner, } from 'ui-kit';
import { useSubscription, useEventSubscription, useAlphalendEventSubscription, useAlphalendSubscription, } from '../hooks';
import { useBalances } from '../context/BalanceContext';
import { useNetworkString } from '../context/NetworkContext';
import { usePositionCap } from '../hooks/usePositionCap';
import { useAddressBalanceGuard } from '../hooks/useAddressBalanceGuard';
import { useAdminAddress } from '../context/AdminAddressContext';
import { getIcon } from '../utils/getIcon';
import { getImage } from '../utils/getImage';
import { transactionHandler } from '../utils/transactionHandlers';
import { commaFy, getDecimalPlaces } from '../utils/currencyUtils';
import { getButtonState } from '../utils/buttonStateUtils';
import { IS_WIND_DOWN_ACTIVE } from '../utils/windDownConfig';
import { getMonthName } from '../utils/dateUtils';
import SwapModal from '../components/SwapModal';
import { SwapButton } from '../components/SwapButton';
const ALPHA_VAULT_POOL_ID = '0x06a4922346ae433e9a2fff4db900d760e0cbfdef748f48385f430ef4d042a6f8';
const SUI_COIN_TYPE = '0x0000000000000000000000000000000000000000000000000000000000000002::sui::SUI';
const AlphaPage = ({ themeName, walletConnectFunctionality }) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y;
    const currentAccount = useCurrentAccount();
    // effectiveAddress is the impersonated address in admin mode, else the
    // connected wallet — used for both data and (dry-run-only) transactions.
    const { effectiveAddress, isAdminMode } = useAdminAddress();
    const { walletBalancesMap } = useBalances();
    const checkAddressBalanceConflict = useAddressBalanceGuard();
    const { currentWallet } = useCurrentWallet();
    const network = useNetworkString();
    const signAndExecuteTransaction = useSignAndExecuteTransaction();
    const { positionCapId } = usePositionCap('single');
    const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
    const [isSwapModalOpen, setIsSwapModalOpen] = useState(false);
    const [swapInitialReceiveSymbol, setSwapInitialReceiveSymbol] = useState(undefined);
    const [tokenValues, setTokenValues] = useState({});
    const [isTransactionLoading, setIsTransactionLoading] = useState(false);
    const [notificationInfo, setNotificationInfo] = useState({
        title: '',
        description: '',
        spinner: false,
        link: '',
        image: undefined,
    });
    const [isNotificationOpen, setIsNotificationOpen] = useState(false);
    const [isMaxClicked, setIsMaxClicked] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const [loadingButtonId, setLoadingButtonId] = useState('');
    const [isClaimRewardsModalOpen, setIsClaimRewardsModalOpen] = useState(false);
    const [myStakeShowUsd, setMyStakeShowUsd] = useState(false);
    const portfolioAddressRef = useRef(null);
    portfolioAddressRef.current = effectiveAddress || null;
    /** Scroll target for hero "Stake ALPHA" → vault staking panel (avoids `document.getElementById`). */
    const vaultSummarySectionRef = useRef(null);
    const { data: staticData } = useEventSubscription('system', 'static_data');
    const { data: protocolData } = useSubscription('protocol');
    const { data: statsData } = useSubscription('stats');
    const { data: portfolioData } = useSubscription(portfolioAddressRef.current ? `portfolio:${portfolioAddressRef.current}` : null);
    const { data: _poolDetailsData } = useSubscription(`pool_details:${ALPHA_VAULT_POOL_ID}`);
    const { data: coinInfoData } = useSubscription('coin_info');
    const { data: marketLowFreqData } = useAlphalendEventSubscription('market_data', 'market_data_low_freq');
    const { data: alphalendPortfolioLowFreqData } = useAlphalendEventSubscription(positionCapId ? `portfolio:${positionCapId}` : null, positionCapId ? `portfolio:${positionCapId}_low_freq` : null);
    const { data: suiMarketDetails } = useAlphalendSubscription('market_details:1');
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 1024);
        };
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);
    const hasBorrowedSui = useMemo(() => {
        if (!(alphalendPortfolioLowFreqData === null || alphalendPortfolioLowFreqData === void 0 ? void 0 : alphalendPortfolioLowFreqData.borrowed_amounts))
            return false;
        const suiBorrowAmount = alphalendPortfolioLowFreqData.borrowed_amounts['1'];
        return suiBorrowAmount !== undefined && parseFloat(suiBorrowAmount) > 0;
    }, [alphalendPortfolioLowFreqData]);
    const alphaTokenData = useMemo(() => {
        var _a, _b, _c;
        const alphaPool = staticData === null || staticData === void 0 ? void 0 : staticData.find((pool) => pool.pool_id === ALPHA_VAULT_POOL_ID);
        const alphaAsset = (_a = alphaPool === null || alphaPool === void 0 ? void 0 : alphaPool.asset_types) === null || _a === void 0 ? void 0 : _a[0];
        const alphaTokenType = (alphaAsset === null || alphaAsset === void 0 ? void 0 : alphaAsset.asset_type) || '';
        const alphaSymbol = (alphaAsset === null || alphaAsset === void 0 ? void 0 : alphaAsset.asset_symbol) || '';
        const decimals = (alphaAsset === null || alphaAsset === void 0 ? void 0 : alphaAsset.asset_decimals) || 9;
        const alphaPrice = ((_b = coinInfoData === null || coinInfoData === void 0 ? void 0 : coinInfoData[alphaTokenType]) === null || _b === void 0 ? void 0 : _b.price) || '0';
        return {
            symbol: alphaSymbol,
            name: 'Alpha Token',
            icon: getIcon('alpha'),
            walletBalance: activeTab === 1
                ? (parseFloat((walletBalancesMap === null || walletBalancesMap === void 0 ? void 0 : walletBalancesMap.get(alphaTokenType)) || '0') / Math.pow(10, decimals)).toString()
                : ((_c = portfolioData === null || portfolioData === void 0 ? void 0 : portfolioData.alpha_vault_portfolio) === null || _c === void 0 ? void 0 : _c.staked_alpha) || '0',
            value: tokenValues[alphaSymbol] || '',
            price: alphaPrice,
            decimals,
            tokenType: alphaTokenType,
        };
    }, [walletBalancesMap, coinInfoData, activeTab, portfolioData, staticData, tokenValues]);
    const suiBalance = useMemo(() => {
        const suiTokenType = '0x2::sui::SUI';
        return (walletBalancesMap === null || walletBalancesMap === void 0 ? void 0 : walletBalancesMap.get(suiTokenType)) || '0';
    }, [walletBalancesMap]);
    const suiPrice = ((_a = coinInfoData === null || coinInfoData === void 0 ? void 0 : coinInfoData[SUI_COIN_TYPE]) === null || _a === void 0 ? void 0 : _a.price) || '0';
    const alphaVaultApr = (_d = (_c = (_b = protocolData === null || protocolData === void 0 ? void 0 : protocolData.pools_data) === null || _b === void 0 ? void 0 : _b[ALPHA_VAULT_POOL_ID]) === null || _c === void 0 ? void 0 : _c.apr) !== null && _d !== void 0 ? _d : '0';
    const chartSeries = useMemo(() => {
        var _a;
        const historicalData = protocolData === null || protocolData === void 0 ? void 0 : protocolData.alpha_vault_historical_data;
        if (!((_a = historicalData === null || historicalData === void 0 ? void 0 : historicalData.historical_apr) === null || _a === void 0 ? void 0 : _a.length) || !(historicalData === null || historicalData === void 0 ? void 0 : historicalData.historical_total_staked))
            return [];
        const stakedByTimestamp = new Map(historicalData.historical_total_staked.map((point) => [
            point.timestamp,
            parseFloat(point.tvl) || 0,
        ]));
        return [...historicalData.historical_apr]
            .sort((a, b) => parseInt(b.timestamp, 10) - parseInt(a.timestamp, 10))
            .map((point) => {
            var _a;
            const date = new Date(parseInt(point.timestamp, 10));
            const dd = String(date.getUTCDate()).padStart(2, '0');
            const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
            const yyyy = date.getUTCFullYear();
            return {
                date: `${dd}/${mm}/${yyyy}`,
                apr: parseFloat(point.apr) || 0,
                totalStaked: (_a = stakedByTimestamp.get(point.timestamp)) !== null && _a !== void 0 ? _a : 0,
            };
        });
    }, [protocolData === null || protocolData === void 0 ? void 0 : protocolData.alpha_vault_historical_data]);
    const realYieldRows = useMemo(() => {
        var _a;
        const airdropHistory = (_a = protocolData === null || protocolData === void 0 ? void 0 : protocolData.alpha_vault_historical_data) === null || _a === void 0 ? void 0 : _a.airdrop_history;
        if (!(airdropHistory === null || airdropHistory === void 0 ? void 0 : airdropHistory.length))
            return [];
        return [...airdropHistory]
            .sort((a, b) => parseInt(b.date, 10) - parseInt(a.date, 10))
            .map((entry) => {
            const date = new Date(parseInt(entry.date, 10));
            const day = String(date.getUTCDate()).padStart(2, '0');
            const month = getMonthName(date.getUTCMonth());
            const year = date.getUTCFullYear();
            return {
                id: entry.date,
                amountLabel: `${commaFy(parseFloat(entry.rewards))} SUI`,
                usersLabel: commaFy(parseFloat(entry.users)),
                dateLabel: `${day} ${month} ${year}`,
            };
        });
    }, [protocolData === null || protocolData === void 0 ? void 0 : protocolData.alpha_vault_historical_data]);
    const thirtyDayProtocolRevenue = useMemo(() => parseFloat((protocolData === null || protocolData === void 0 ? void 0 : protocolData.thirty_day_revenue) || (statsData === null || statsData === void 0 ? void 0 : statsData.thirty_day_revenue) || '0'), [protocolData === null || protocolData === void 0 ? void 0 : protocolData.thirty_day_revenue, statsData === null || statsData === void 0 ? void 0 : statsData.thirty_day_revenue]);
    const lifetimeRevenueDisplay = useMemo(() => {
        const raw = (protocolData === null || protocolData === void 0 ? void 0 : protocolData.lifetime_revenue) || (statsData === null || statsData === void 0 ? void 0 : statsData.lifetime_revenue) || '0';
        return `$${convertToKAndM(raw)}`;
    }, [protocolData === null || protocolData === void 0 ? void 0 : protocolData.lifetime_revenue, statsData === null || statsData === void 0 ? void 0 : statsData.lifetime_revenue]);
    const handleClaimRewardsButtonClick = useCallback(() => {
        var _a;
        if (parseFloat(((_a = portfolioData === null || portfolioData === void 0 ? void 0 : portfolioData.alpha_vault_portfolio) === null || _a === void 0 ? void 0 : _a.claimable_airdrop) || '0') <= 0) {
            return;
        }
        setIsClaimRewardsModalOpen(true);
    }, [(_e = portfolioData === null || portfolioData === void 0 ? void 0 : portfolioData.alpha_vault_portfolio) === null || _e === void 0 ? void 0 : _e.claimable_airdrop]);
    const handleClaimRewards = useCallback((supplyInMarket, repayInMarket) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        if (!effectiveAddress)
            return;
        setLoadingButtonId('collectReward');
        setIsClaimRewardsModalOpen(false);
        try {
            yield transactionHandler({
                poolName: ((_a = staticData === null || staticData === void 0 ? void 0 : staticData.find((pool) => pool.pool_id === ALPHA_VAULT_POOL_ID)) === null || _a === void 0 ? void 0 : _a.pool_name) ||
                    'Alpha Vault',
                amount: '0',
                poolId: ALPHA_VAULT_POOL_ID,
                decimals: '9',
                network: network,
                action: 'claim_reward',
                modalState: {
                    isOpen: true,
                    token: alphaTokenData,
                    actionType: 'claim_reward',
                },
                address: effectiveAddress,
                signAndExecuteTransaction: signAndExecuteTransaction,
                setNotificationInfo: setNotificationInfo,
                setIsNotificationOpen: setIsNotificationOpen,
                setIsTransactionLoading: setIsTransactionLoading,
                displayAmount: `${parseFloat(((_b = portfolioData === null || portfolioData === void 0 ? void 0 : portfolioData.alpha_vault_portfolio) === null || _b === void 0 ? void 0 : _b.claimable_airdrop) || '0').toFixed(4)} SUI`,
                suiBalance: suiBalance,
                resetInputValues: () => {
                    setLoadingButtonId('');
                },
                supplyInMarket,
                repayInMarket,
            });
        }
        catch (error) {
            console.error('Collect rewards failed:', error);
            setLoadingButtonId('');
        }
    }), [
        effectiveAddress,
        alphaTokenData,
        suiBalance,
        signAndExecuteTransaction,
        network,
        (_f = portfolioData === null || portfolioData === void 0 ? void 0 : portfolioData.alpha_vault_portfolio) === null || _f === void 0 ? void 0 : _f.claimable_airdrop,
        staticData,
    ]);
    const collectRewardDisplay = useMemo(() => {
        var _a, _b, _c;
        const claim = ((_a = portfolioData === null || portfolioData === void 0 ? void 0 : portfolioData.alpha_vault_portfolio) === null || _a === void 0 ? void 0 : _a.claimable_airdrop) || '0';
        const claimed = ((_b = portfolioData === null || portfolioData === void 0 ? void 0 : portfolioData.alpha_vault_portfolio) === null || _b === void 0 ? void 0 : _b.total_airdrop_claimed) || '0';
        const staked = ((_c = portfolioData === null || portfolioData === void 0 ? void 0 : portfolioData.alpha_vault_portfolio) === null || _c === void 0 ? void 0 : _c.staked_alpha) || '0';
        const alphaPrice = (alphaTokenData === null || alphaTokenData === void 0 ? void 0 : alphaTokenData.price) || '0';
        let rewardAmount = '0';
        if (parseFloat(claim) > 1) {
            rewardAmount = convertToKAndM(claim, { decimals: 2 }).toString();
        }
        else if (parseFloat(claim) > 0) {
            rewardAmount = convertToKAndM(claim, { decimals: 4 }).toString();
        }
        else {
            rewardAmount = '0';
        }
        const claimUsd = parseFloat(claim) * parseFloat(suiPrice);
        const rewardAmountUsd = claimUsd > 1
            ? '$' + convertToKAndM(claimUsd.toString(), { decimals: 2 })
            : '$' + convertToKAndM(claimUsd.toString(), { decimals: 4 });
        let myStake = '0';
        if (parseFloat(staked) > 1) {
            myStake = convertToKAndM(staked, { decimals: 2 }).toString();
        }
        else if (parseFloat(staked) > 0) {
            myStake = convertToKAndM(staked, { decimals: 4 }).toString();
        }
        let totalClaimed = '0';
        if (parseFloat(claimed) > 1) {
            totalClaimed = convertToKAndM(claimed, { decimals: 2 }).toString();
        }
        else if (parseFloat(claimed) > 0) {
            totalClaimed = convertToKAndM(claimed, { decimals: 4 }).toString();
        }
        const stakedUsd = parseFloat(staked) * parseFloat(alphaPrice);
        const myStakeUsdDisplay = stakedUsd > 1
            ? '$' + convertToKAndM(stakedUsd.toString(), { decimals: 2 })
            : '$' + convertToKAndM(stakedUsd.toString(), { decimals: 4 });
        return {
            rewardAmount,
            rewardAmountUsd,
            myStakeTokenDisplay: myStake,
            myStakeUsdDisplay,
            totalClaimed,
            actualClaim: claim,
        };
    }, [
        (_g = portfolioData === null || portfolioData === void 0 ? void 0 : portfolioData.alpha_vault_portfolio) === null || _g === void 0 ? void 0 : _g.claimable_airdrop,
        (_h = portfolioData === null || portfolioData === void 0 ? void 0 : portfolioData.alpha_vault_portfolio) === null || _h === void 0 ? void 0 : _h.staked_alpha,
        (_j = portfolioData === null || portfolioData === void 0 ? void 0 : portfolioData.alpha_vault_portfolio) === null || _j === void 0 ? void 0 : _j.total_airdrop_claimed,
        alphaTokenData === null || alphaTokenData === void 0 ? void 0 : alphaTokenData.price,
        suiPrice,
    ]);
    const updatedPendingWithdrawals = useMemo(() => {
        var _a, _b, _c;
        if (effectiveAddress &&
            ((_a = portfolioData === null || portfolioData === void 0 ? void 0 : portfolioData.alpha_vault_portfolio) === null || _a === void 0 ? void 0 : _a.pending_withdrawals) &&
            ((_b = portfolioData === null || portfolioData === void 0 ? void 0 : portfolioData.alpha_vault_portfolio) === null || _b === void 0 ? void 0 : _b.pending_withdrawals.length) > 0) {
            const list = (_c = portfolioData === null || portfolioData === void 0 ? void 0 : portfolioData.alpha_vault_portfolio) === null || _c === void 0 ? void 0 : _c.pending_withdrawals.map((withdrawal) => {
                const withdrawalDate = withdrawal.withdrawal_eta
                    ? new Date(parseFloat(withdrawal.withdrawal_eta))
                    : null;
                const date = withdrawalDate
                    ? withdrawalDate
                        .toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric',
                    })
                        .replace(/,/g, '')
                    : null;
                return Object.assign(Object.assign({}, withdrawal), { icon: getIcon('alpha'), withdrawal_eta: date });
            });
            return list.reverse();
        }
        return null;
    }, [effectiveAddress, (_k = portfolioData === null || portfolioData === void 0 ? void 0 : portfolioData.alpha_vault_portfolio) === null || _k === void 0 ? void 0 : _k.pending_withdrawals]);
    const handleTabChange = (tabIndex) => {
        setActiveTab(tabIndex);
        setTokenValues({});
        setIsMaxClicked(false);
    };
    const handleTokenValueChange = (tokenSymbol, value) => {
        setTokenValues((prev) => (Object.assign(Object.assign({}, prev), { [tokenSymbol]: value })));
        setIsMaxClicked(false);
    };
    const handleMaxClick = (tokenSymbol) => {
        if (!alphaTokenData || tokenSymbol !== alphaTokenData.symbol)
            return;
        const balance = parseFloat(alphaTokenData.walletBalance);
        if (isNaN(balance) || !isFinite(balance) || balance <= 0)
            return;
        const tokenDecimals = alphaTokenData.decimals;
        const multiplier = Math.pow(10, tokenDecimals);
        const maxValue = (Math.floor(balance * multiplier) / multiplier).toString();
        handleTokenValueChange(tokenSymbol, maxValue);
        setIsMaxClicked(true);
    };
    const handleDeposit = () => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b;
        if (!effectiveAddress || !alphaTokenData || !tokenValues[alphaTokenData.symbol])
            return;
        // Wind-down mode: never build a stake transaction. Unstaking stays open.
        if (IS_WIND_DOWN_ACTIVE) {
            console.error('Staking is paused while AlphaFi winds down');
            return;
        }
        if (!isAdminMode && checkAddressBalanceConflict([{ coinType: alphaTokenData.tokenType }])) {
            setNotificationInfo({
                title: 'Wallet Update Required',
                description: `${(_a = currentWallet === null || currentWallet === void 0 ? void 0 : currentWallet.name) !== null && _a !== void 0 ? _a : 'Your wallet'} doesn't yet support address balances, a recent Sui network update. Your funds are safe — to continue, connect a supported wallet.`,
                image: getIcon('failed'),
                spinner: false,
            });
            setIsNotificationOpen(true);
            return;
        }
        setLoadingButtonId('stake');
        const amount = tokenValues[alphaTokenData.symbol];
        const formattedAmount = `${parseFloat(amount).toFixed(getDecimalPlaces(alphaTokenData.symbol))} ${alphaTokenData.symbol} `;
        try {
            yield transactionHandler({
                poolName: ((_b = staticData === null || staticData === void 0 ? void 0 : staticData.find((pool) => pool.pool_id === ALPHA_VAULT_POOL_ID)) === null || _b === void 0 ? void 0 : _b.pool_name) ||
                    'Alpha Vault',
                amount: amount,
                poolId: ALPHA_VAULT_POOL_ID,
                decimals: String(alphaTokenData.decimals),
                network: network,
                action: 'stake',
                modalState: {
                    isOpen: true,
                    token: alphaTokenData,
                    actionType: 'stake',
                },
                address: effectiveAddress,
                signAndExecuteTransaction: signAndExecuteTransaction,
                setNotificationInfo: setNotificationInfo,
                setIsNotificationOpen: setIsNotificationOpen,
                setIsTransactionLoading: setIsTransactionLoading,
                displayAmount: formattedAmount,
                suiBalance: suiBalance,
                isAmountA: true,
                resetInputValues: () => {
                    setTokenValues({});
                    setIsMaxClicked(false);
                },
            });
            setLoadingButtonId('');
        }
        catch (error) {
            console.error('Stake failed:', error);
            setLoadingButtonId('');
        }
    });
    const handleWithdraw = () => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        if (!effectiveAddress || !alphaTokenData || !tokenValues[alphaTokenData.symbol])
            return;
        setLoadingButtonId('unstake');
        const amount = tokenValues[alphaTokenData.symbol];
        const formattedAmount = `${parseFloat(amount).toFixed(getDecimalPlaces(alphaTokenData.symbol))} ${alphaTokenData.symbol} `;
        try {
            yield transactionHandler({
                poolName: ((_a = staticData === null || staticData === void 0 ? void 0 : staticData.find((pool) => pool.pool_id === ALPHA_VAULT_POOL_ID)) === null || _a === void 0 ? void 0 : _a.pool_name) ||
                    'Alpha Vault',
                amount: amount,
                poolId: ALPHA_VAULT_POOL_ID,
                decimals: String(alphaTokenData.decimals),
                network: network,
                action: 'unstake',
                modalState: {
                    isOpen: true,
                    token: alphaTokenData,
                    actionType: 'unstake',
                },
                address: effectiveAddress,
                signAndExecuteTransaction: signAndExecuteTransaction,
                setNotificationInfo: setNotificationInfo,
                setIsNotificationOpen: setIsNotificationOpen,
                setIsTransactionLoading: setIsTransactionLoading,
                displayAmount: formattedAmount,
                suiBalance: suiBalance,
                isAmountA: true,
                withdrawMax: isMaxClicked,
                resetInputValues: () => {
                    setTokenValues({});
                    setIsMaxClicked(false);
                },
            });
            setLoadingButtonId('');
        }
        catch (error) {
            console.error('Unstake failed:', error);
            setLoadingButtonId('');
        }
    });
    const handleClaimPendingWithdrawals = (ticketId, withdrawalAmount) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        if (!effectiveAddress || !alphaTokenData)
            return;
        setLoadingButtonId(ticketId);
        const formattedAmount = `${parseFloat(withdrawalAmount).toFixed(getDecimalPlaces(alphaTokenData.symbol))} ${alphaTokenData.symbol} `;
        try {
            yield transactionHandler({
                poolName: ((_a = staticData === null || staticData === void 0 ? void 0 : staticData.find((pool) => pool.pool_id === ALPHA_VAULT_POOL_ID)) === null || _a === void 0 ? void 0 : _a.pool_name) ||
                    'Alpha Vault',
                amount: withdrawalAmount,
                poolId: ALPHA_VAULT_POOL_ID,
                decimals: String(alphaTokenData.decimals),
                network: network,
                action: 'claim_unstake',
                modalState: {
                    isOpen: true,
                    token: alphaTokenData,
                    actionType: 'claim_unstake',
                },
                address: effectiveAddress,
                signAndExecuteTransaction: signAndExecuteTransaction,
                setNotificationInfo: setNotificationInfo,
                setIsNotificationOpen: setIsNotificationOpen,
                setIsTransactionLoading: setIsTransactionLoading,
                displayAmount: formattedAmount,
                suiBalance: suiBalance,
                isAmountA: true,
                withdrawMax: isMaxClicked,
                resetInputValues: () => {
                    setTokenValues({});
                    setIsMaxClicked(false);
                },
                ticketId: ticketId,
            });
            setLoadingButtonId('');
        }
        catch (error) {
            console.error('Claim unstake failed:', error);
            setLoadingButtonId('');
        }
    });
    const poolActionTabIndex = activeTab === 1 ? 0 : activeTab === 2 ? 1 : 0;
    const buttonState = useMemo(() => {
        if (!alphaTokenData)
            return { disabled: true, text: 'Stake' };
        if (activeTab === 0)
            return { disabled: true, text: 'Stake' };
        return getButtonState({
            inputValues: tokenValues,
            tabIndex: poolActionTabIndex,
            tokens: [alphaTokenData],
            maxClicked: isMaxClicked,
            // poolActionTabIndex 0 is the Stake tab, which wind-down mode closes.
            depositDisabled: IS_WIND_DOWN_ACTIVE,
        });
    }, [tokenValues, activeTab, alphaTokenData, isMaxClicked, poolActionTabIndex]);
    const navTokens = alphaTokenData
        ? [
            {
                symbol: alphaTokenData.symbol,
                name: alphaTokenData.name,
                icon: alphaTokenData.icon,
                walletBalance: alphaTokenData.walletBalance,
                value: alphaTokenData.value,
                price: alphaTokenData.price,
            },
        ]
        : [
            {
                symbol: 'ALPHA',
                name: 'Alpha Token',
                icon: getIcon('alpha'),
                walletBalance: '0',
                value: '',
                price: '0',
            },
        ];
    const navTabsProps = {
        tokens: navTokens,
        activeTab,
        depositTabIndex: 1,
        onTabChange: handleTabChange,
        onTokenValueChange: handleTokenValueChange,
        onMaxClick: handleMaxClick,
        onDeposit: () => void handleDeposit(),
        onWithdraw: () => void handleWithdraw(),
        isLoading: isTransactionLoading,
        isButtonDisabled: buttonState.disabled,
        isPoolDescription: true,
        isPoolPage: true,
        balanceText: activeTab === 1 ? 'In Wallet:' : 'In Vault:',
        buttonText: !effectiveAddress ? 'Connect Wallet' : activeTab === 1 ? 'Stake' : 'Unstake',
        alphaVaultInfoText: true,
        walletConnectFunctionality: !effectiveAddress ? walletConnectFunctionality : undefined,
        navTabs: [
            {
                label: 'Collect Rewards',
                disabled: false,
                isCollectReward: true,
            },
            { label: 'Stake', disabled: IS_WIND_DOWN_ACTIVE },
            { label: 'Unstake', disabled: false },
        ],
        collectRewardSection: {
            rewardAmount: collectRewardDisplay.rewardAmount,
            rewardAmountUsd: collectRewardDisplay.rewardAmountUsd,
            rewardIcon: getIcon('sui'),
            myStake: myStakeShowUsd
                ? collectRewardDisplay.myStakeUsdDisplay
                : collectRewardDisplay.myStakeTokenDisplay,
            myStakeIcon: getIcon('alpha'),
            myStakeDollarSwitchIcon: themeName === 'dark' ? getIcon('switchDark') : getIcon('switchLight'),
            onMyStakeDollarSwitchClick: () => setMyStakeShowUsd((v) => !v),
            totalClaimed: collectRewardDisplay.totalClaimed,
            totalClaimedIcon: getIcon('sui'),
            avgApr: `${Number(alphaVaultApr).toFixed(2)}%`,
            onCollectRewards: handleClaimRewardsButtonClick,
            collectRewardsButtonText: 'Collect Rewards',
            isWalletConnected: !!effectiveAddress,
            walletConnectFunctionality,
            collectButtonDisabled: parseFloat(collectRewardDisplay.actualClaim) <= 0,
        },
        alphaVectorImage: themeName === 'dark' ? getImage('alphaVectorDark') : getImage('alphaVectorLight'),
        // buttonSubtext: 'Yield generation will begin from Sunday.',
        pendingWithdrawals: updatedPendingWithdrawals,
        claimPendingWithdrawals: handleClaimPendingWithdrawals,
        loadingButtonId,
        downArrowIcon: themeName === 'dark' ? getIcon('whiteDownArrow') : getIcon('blackDownArrow'),
    };
    const openSwapModal = (initialReceiveSymbol) => {
        setSwapInitialReceiveSymbol(initialReceiveSymbol);
        setIsSwapModalOpen(true);
    };
    const handleSwapModalClose = () => {
        setIsSwapModalOpen(false);
        setSwapInitialReceiveSymbol(undefined);
    };
    const handleSwapNotification = (info) => {
        setNotificationInfo({
            title: info.title,
            description: info.description,
            spinner: info.spinner || false,
            link: info.link || '',
            image: info.image || undefined,
        });
        setIsNotificationOpen(true);
    };
    const totalStakedValue = (_m = (_l = protocolData === null || protocolData === void 0 ? void 0 : protocolData.pools_data) === null || _l === void 0 ? void 0 : _l[ALPHA_VAULT_POOL_ID]) === null || _m === void 0 ? void 0 : _m.tvl_usd;
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "theme-bg-vote-page flex flex-col xl:gap-y-20 2xl:gap-y-52 pb-10 md:pb-24", children: [_jsxs("div", { className: `flex ${Number((_o = portfolioData === null || portfolioData === void 0 ? void 0 : portfolioData.alpha_vault_portfolio) === null || _o === void 0 ? void 0 : _o.staked_alpha) > 0 ? 'flex-col-reverse' : 'flex-col'}`, children: [_jsx(AlphaStakingHero, { headline: "Own the Revenue of\nSui's Fastest Growing DeFi Stack", subtitle: "Earn from every transaction across AlphaFi \u2014 with rewards paid directly to ALPHA stakers.", stats: [
                                    {
                                        label: 'Protocol Revenue (30D)',
                                        mobileLabel: '30D Revenue',
                                        value: `$${commaFy(Math.round(thirtyDayProtocolRevenue))}`,
                                    },
                                    {
                                        label: 'ALPHA Staking APR',
                                        mobileLabel: 'Staking APR',
                                        value: `${Number(alphaVaultApr).toFixed(2)}%`,
                                    },
                                    {
                                        label: 'Total Staked',
                                        mobileLabel: 'Total Staked',
                                        value: `$${convertToKAndM(totalStakedValue || '0')}`,
                                    },
                                ], onStakeClick: () => {
                                    var _a;
                                    handleTabChange(1);
                                    (_a = vaultSummarySectionRef.current) === null || _a === void 0 ? void 0 : _a.scrollIntoView({
                                        behavior: 'smooth',
                                        block: 'start',
                                    });
                                }, onBuyClick: () => openSwapModal('ALPHA'), stakeButtonText: "Stake ALPHA", buyButtonText: "Buy ALPHA", logoImage: getImage('alphaLogo') }), _jsx("div", { ref: vaultSummarySectionRef, className: "my-5 md:my-8", children: _jsx(AlphaVaultSummary, { header: {
                                        logoSrc: getIcon('alpha'),
                                        title: 'ALPHA Vault',
                                        totalStakedValue: `$${convertToKAndM(totalStakedValue || '0')}`,
                                        stakersValue: commaFy(17000 +
                                            (Number((_q = (_p = protocolData === null || protocolData === void 0 ? void 0 : protocolData.pools_data) === null || _p === void 0 ? void 0 : _p[ALPHA_VAULT_POOL_ID]) === null || _q === void 0 ? void 0 : _q.total_stakers) || 0)),
                                    }, chartSeries: chartSeries, navTabsProps: navTabsProps }) })] }), _jsx("div", { className: "mx-auto mt-10 md:mt-0 px-5 lg:px-0", children: _jsx(RealYieldTable, { headerIcon: getIcon('realYieldIcon'), usersIcon: getIcon(themeName === 'dark' ? 'groupLightIcon' : 'groupDarkIcon'), defaultAmountTokenIcon: getIcon('sui'), filterIcon: themeName === 'dark'
                                ? getIcon('switchTokenDarkThemeIcon')
                                : getIcon('switchTokenIcon'), title: "Real Yield. Paid Weekly.", subtitle: "No emissions. No inflation. Pure protocol revenue.", rows: realYieldRows, columnLabels: { amount: 'Rewards', users: 'Users', date: 'Date' }, initialVisibleRowCount: 4 }) }), _jsx("div", { className: "px-5 sm:!px-40 lg:!px-0 mt-20 xl:mt-0", children: _jsx("img", { src: isMobile
                                ? themeName === 'dark'
                                    ? getImage('whyAlphaDarkMobile')
                                    : getImage('whyAlphaLightMobile')
                                : themeName === 'dark'
                                    ? getImage('whyAlphaDark')
                                    : getImage('whyAlphaLight'), alt: "Why Alpha", className: "mx-auto object-contain" }) }), _jsx("div", { className: "my-20 md:my-32 px-2 md:px-0 w-full h-full", children: _jsx(AlphaTokenFlywheel, { ellipseBackdropSrc: themeName === 'dark'
                                ? getImage('flywheelEllipseDark')
                                : getImage('flywheelEllipseLight'), title: "Alpha Token Flywheel", subtitle: "The more AlphaFi grows, the more ALPHA earns.", centerIcon: getIcon('borderedAlphaLogo'), themeName: themeName, isMobile: isMobile, nodes: [
                                {
                                    id: 'revenue',
                                    label: 'More Revenue',
                                    angle: 0,
                                    icon: getIcon('flyWheelIcon2'),
                                },
                                {
                                    id: 'usage',
                                    label: 'More Usage',
                                    angle: 90,
                                    icon: getIcon('flyWheelIcon1'),
                                },
                                {
                                    id: 'demand',
                                    label: 'Stronger Demand',
                                    angle: 180,
                                    icon: getIcon('flyWheelIcon4'),
                                },
                                {
                                    id: 'rewards',
                                    label: 'More Rewards',
                                    angle: 270,
                                    icon: getIcon('flyWheelIcon3'),
                                },
                            ] }) }), _jsx("div", { className: "mb-16 md:mb-0 mt-12 md:mt-0", children: _jsx(GrowthMetrics, { title: "Growth Metrics", subtitle: "Real traction driving real revenue", metrics: [
                                {
                                    icon: themeName === 'dark' ? getIcon('tvlDark') : getIcon('tvlLight'),
                                    value: `$${commaFy(parseFloat((protocolData === null || protocolData === void 0 ? void 0 : protocolData.tvl.total_tvl) || '0').toFixed())}`,
                                    label: 'TVL',
                                },
                                {
                                    icon: themeName === 'dark' ? getIcon('percentDark') : getIcon('percentLight'),
                                    value: `$${commaFy(Math.round(thirtyDayProtocolRevenue))}`,
                                    label: 'Protocol Fees (30D)',
                                },
                                {
                                    icon: themeName === 'dark' ? getIcon('usersDark') : getIcon('usersLight'),
                                    value: `${commaFy(Number((suiMarketDetails === null || suiMarketDetails === void 0 ? void 0 : suiMarketDetails.total_suppliers) || 0))}`,
                                    label: 'Total Users',
                                },
                                {
                                    icon: themeName === 'dark' ? getIcon('moneyBagDark') : getIcon('moneyBagLight'),
                                    value: lifetimeRevenueDisplay,
                                    label: 'Lifetime Revenue',
                                },
                            ] }) }), _jsx("div", { className: "px-5 sm:!px-28 lg:!px-44 xl:!px-0", children: _jsx(TokenDistributionSupplySection, { distributionImageMobile: themeName === 'dark'
                                ? getImage('tokenDistributionMobileDark')
                                : getImage('tokenDistributionMobileLight'), distributionImageDesktop: themeName === 'dark'
                                ? getImage('tokenDistributionDark')
                                : getImage('tokenDistributionLight'), circulatingSupply: commaFy(parseFloat(((_r = statsData === null || statsData === void 0 ? void 0 : statsData.alpha_breakdown) === null || _r === void 0 ? void 0 : _r.circulating_supply) || '0').toFixed()), stakedSupply: commaFy(parseFloat(((_t = (_s = protocolData === null || protocolData === void 0 ? void 0 : protocolData.pools_data) === null || _s === void 0 ? void 0 : _s[ALPHA_VAULT_POOL_ID]) === null || _t === void 0 ? void 0 : _t.tvl) || '0').toFixed()), marketCap: `$${commaFy(parseFloat((statsData === null || statsData === void 0 ? void 0 : statsData.alpha_market_cap) || '0').toFixed())}`, maxSupply: commaFy(parseFloat(((_u = statsData === null || statsData === void 0 ? void 0 : statsData.alpha_breakdown) === null || _u === void 0 ? void 0 : _u.max_supply) || '0').toFixed()), title: "Supply Overview", decorativeVectorSrc: themeName === 'dark' ? getImage('alphaVectorDark') : getImage('alphaVectorLight') }) }), _jsx("div", { className: "mb-10 px-5 sm:!px-40 lg:!px-0 mt-20 xl:mt-0", children: _jsx("img", { src: isMobile
                                ? themeName === 'dark'
                                    ? getImage('moreUsageMobileDark')
                                    : getImage('moreUsageMobileLight')
                                : themeName === 'dark'
                                    ? getImage('moreUsageDark')
                                    : getImage('moreUsageLight'), alt: "More Usage", className: "mx-auto object-contain" }) }), _jsx(YieldBanner, { title: "Start Earning Yield", description: "(Up to 20% APR from real protocol revenue)", buttonLabel: "Buy ALPHA", onButtonClick: () => openSwapModal('ALPHA') })] }), _jsx(SwapButton, { isSwapModalOpen: isSwapModalOpen, onClick: () => openSwapModal(undefined), themeName: themeName }), _jsx(SwapModal, { isOpen: isSwapModalOpen, onClose: handleSwapModalClose, onNotification: handleSwapNotification, loading: isTransactionLoading, setLoading: setIsTransactionLoading, walletConnectFunctionality: walletConnectFunctionality, isWalletConnected: !!(currentAccount === null || currentAccount === void 0 ? void 0 : currentAccount.address), theme: themeName, initialReceiveSymbol: swapInitialReceiveSymbol }), _jsx(NotificationComp, { isOpen: isNotificationOpen, setIsOpen: setIsNotificationOpen, info: notificationInfo }), _jsx(ClaimRewards, { isOpen: isClaimRewardsModalOpen, onClose: () => setIsClaimRewardsModalOpen(false), reward: {
                    amount: ((_v = portfolioData === null || portfolioData === void 0 ? void 0 : portfolioData.alpha_vault_portfolio) === null || _v === void 0 ? void 0 : _v.claimable_airdrop) || '0',
                    usdValue: (parseFloat(((_w = portfolioData === null || portfolioData === void 0 ? void 0 : portfolioData.alpha_vault_portfolio) === null || _w === void 0 ? void 0 : _w.claimable_airdrop) || '0') *
                        parseFloat(suiPrice)).toString(),
                    icon: getIcon('sui'),
                }, onClaimRewards: handleClaimRewards, closeIcon: themeName === 'dark' ? getIcon('darkCloseIcon') : getIcon('close'), apr: !hasBorrowedSui
                    ? `earn ${(Number((_y = (_x = marketLowFreqData === null || marketLowFreqData === void 0 ? void 0 : marketLowFreqData.markets) === null || _x === void 0 ? void 0 : _x['1']) === null || _y === void 0 ? void 0 : _y.supply_apr.total) || 0).toFixed(2)}% APR`
                    : '', supplyCheckbox: {
                    enabled: !hasBorrowedSui,
                    text: 'Collect & Supply in SUI Market to',
                }, repayCheckbox: {
                    enabled: hasBorrowedSui,
                    text: 'Collect & Repay SUI. Any remaining SUI goes to your wallet.',
                } })] }));
};
export default AlphaPage;
